import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Minus, Plus, ShoppingCart } from 'lucide-react';
import type { TenantSummary } from '@forq/contracts';
import type { PartsGateway } from '@forq/parts-data';
import { mockCatalogue, searchMockCatalogue } from './mockSupplier';

/**
 * Der Übungsshop -- ein Lieferantenshop, den FORQ selbst spielt.
 *
 * Er existiert, damit der ganze Ablauf ohne einen echten Lieferanten durchgespielt werden kann:
 * Suche, Warenkorb, Rückgabe an FORQ. Er verhält sich absichtlich wie ein FREMDER Shop -- er
 * bekommt eine Sitzungsmarke in der Adresse und schickt seinen Warenkorb an den Rückgabeweg,
 * genau wie TVH oder TREX es täten.
 *
 * Was er NICHT tut und niemals tun wird: bestellen. Auch hier löst der Warenkorb nur einen
 * Entwurf in FORQ aus. Bestellt wird ausschließlich vom FORQ-Server.
 */

interface UebungsshopPageProps {
  readonly tenant: TenantSummary;
  readonly gateway: PartsGateway;
}

function money(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message
    ? error.message
    : 'Der Warenkorb konnte nicht an FORQ zurückgegeben werden.';
}

export function UebungsshopPage({ tenant, gateway }: UebungsshopPageProps) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const marke = params.get('marke') ?? '';
  const vorgang = params.get('vorgang') ?? '';

  const [query, setQuery] = useState('');
  const [cart, setCart] = useState<Readonly<Record<string, number>>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const treffer = useMemo(() => searchMockCatalogue(query), [query]);
  const zeilen = useMemo(
    () =>
      Object.entries(cart)
        .filter(([, menge]) => menge > 0)
        .map(([nummer, menge]) => ({
          nummer,
          menge,
          eintrag: mockCatalogue.find((entry) => entry.supplierItemNumber === nummer)!,
        })),
    [cart],
  );
  const summe = zeilen.reduce((sum, zeile) => sum + zeile.menge * zeile.eintrag.unitPrice, 0);

  const setMenge = useCallback((nummer: string, delta: number) => {
    setCart((aktuell) => ({ ...aktuell, [nummer]: Math.max(0, (aktuell[nummer] ?? 0) + delta) }));
  }, []);

  const zurueckgeben = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const entwurf = await gateway.completePunchout({
        tenantId: tenant.id,
        sessionToken: marke,
        lines: zeilen.map((zeile) => ({ supplierItemNumber: zeile.nummer, quantity: zeile.menge })),
      });
      setDone(entwurf.number);
      setCart({});
    } catch (returnError) {
      setError(messageOf(returnError));
    } finally {
      setBusy(false);
    }
  }, [gateway, tenant.id, marke, zeilen]);

  if (!marke) {
    return (
      <div className="parts-shop">
        <div className="parts-shop__banner">Übungslieferant</div>
        <p className="parts-shop__hint" role="alert">
          Diese Seite wird nur über den Knopf „Ersatzteil suchen" in einem Vorgang aufgerufen. Ohne
          Sitzungsmarke lässt sich kein Warenkorb zurückgeben.
        </p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="parts-shop">
        <div className="parts-shop__banner">Übungslieferant</div>
        <div className="parts-shop__done" role="status">
          <h2>Warenkorb an FORQ übergeben</h2>
          <p>
            Daraus ist der Bestellentwurf <strong>{done}</strong> geworden. Beim Lieferanten wurde
            nichts bestellt -- das macht erst FORQ, nach der Prüfung.
          </p>
          <button
            type="button"
            className="parts-shop__submit"
            onClick={() => navigate(vorgang ? `/vorgaenge/${vorgang}` : '/ersatzteile')}
          >
            Zurück zum Vorgang {vorgang}
          </button>
          <p className="parts-shop__hint">
            Bei einem echten Lieferanten steht hier nichts: dessen Shop läuft in einem eigenen
            Fenster, und der Warenkorb kommt ohne den Browser zum FORQ-Server zurück.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="parts-shop">
      <div className="parts-shop__banner">
        Übungslieferant · Sitzung zu Vorgang {vorgang || 'unbekannt'}
      </div>

      {error ? (
        <div className="parts-shop__error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="parts-shop__layout">
        <section>
          <input
            type="search"
            value={query}
            placeholder="Artikelnummer, Bezeichnung oder Hersteller"
            onChange={(event) => setQuery(event.target.value)}
          />
          <ul className="parts-shop__list">
            {treffer.map((eintrag) => (
              <li key={eintrag.supplierItemNumber}>
                <div>
                  <strong>{eintrag.description}</strong>
                  <span>
                    {eintrag.supplierItemNumber} · {eintrag.manufacturer} ·{' '}
                    {eintrag.manufacturerPartNumber}
                  </span>
                  <span>
                    {money(eintrag.unitPrice)} / {eintrag.unit} ·{' '}
                    {eintrag.leadTimeDays <= 1 ? 'ab Lager' : `${eintrag.leadTimeDays} Tage`}
                  </span>
                </div>
                <div className="parts-shop__stepper">
                  <button type="button" onClick={() => setMenge(eintrag.supplierItemNumber, -1)}>
                    <Minus size={14} />
                  </button>
                  <span>{cart[eintrag.supplierItemNumber] ?? 0}</span>
                  <button type="button" onClick={() => setMenge(eintrag.supplierItemNumber, 1)}>
                    <Plus size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <aside className="parts-shop__cart">
          <h3>
            <ShoppingCart size={15} /> Warenkorb
          </h3>
          {zeilen.length === 0 ? (
            <p>Noch nichts ausgewählt.</p>
          ) : (
            <ul>
              {zeilen.map((zeile) => (
                <li key={zeile.nummer}>
                  <span>
                    {zeile.menge} × {zeile.eintrag.description}
                  </span>
                  <span>{money(zeile.menge * zeile.eintrag.unitPrice)}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="parts-shop__sum">
            Zwischensumme <strong>{money(summe)}</strong>
          </p>
          <button
            type="button"
            className="parts-shop__submit"
            disabled={busy || zeilen.length === 0}
            onClick={() => void zurueckgeben()}
          >
            {busy ? 'Wird übergeben …' : 'Warenkorb an FORQ übergeben'}
          </button>
          <p className="parts-shop__hint">
            Dabei wird beim Lieferanten nichts bestellt. In FORQ entsteht ein Entwurf, den jemand mit
            Bestellrecht prüfen und absenden muss.
          </p>
        </aside>
      </div>
    </div>
  );
}
